import { OutputData } from '@editorjs/editorjs';
import { Project } from '../../types/items';
import InstructionGenerator from './InstructionGenerator';
import { NameScript } from '../../configs/instructionConfig';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type Block = {
  type: BlockType;
  data: BlockData;
};

export type BlockData = {
  text?: string;
  caption?: string;
  level?: string;
  items?: string[];
  content?: string[][];
  [key: string]: any;
};

export enum BlockType {
  PARAGRAPH = 'paragraph',
  HEADER = 'header',
  CODE = 'code',
  DELIMETER = 'delimeter',
  TABLE = 'table',
  LIST = 'list',
  IMAGE = 'image',
  RAW = 'raw',
  ATTACHES = 'attaches',
  EMBED = 'embed',
  NAME_SCRIPT = 'nameScriptInstructionBlock',
}

export type EditorJsParserParams = {
  project: Project | null;
};

class EditorJsParser {
  protected classes = {
    container: 'ce-block',
    content: 'ce-block__content',
    block: 'cdx-block',
    paragraph: 'ce-paragraph cdx-block',
    header: 'ce-header',
    code: 'ce-code',
    raw: 'ce-code',
    list: 'cdx-block cdx-list',
    unorderedList: 'cdx-list--unordered',
    orderedList: 'cdx-list--ordered',
    listItem: 'cdx-list__item',
    delimeter: 'ce-delimiter cdx-block',
    table: 'tc-table',
    tableWrap: 'tc-table__wrap',
    cell: 'tc-table__cell',
    cellContent: 'tc-table__area',
  };

  protected html: string;

  protected blocks: Block[];

  protected params: EditorJsParserParams | undefined;

  constructor(data: OutputData, params?: EditorJsParserParams) {
    this.html = '';
    this.blocks = data.blocks as Block[];
    this.params = params;
  }

  public parse() {
    this.blocks.forEach((block) => {
      this.generator(block);
    });

    return this.html;
  }

  protected get blockTypeContainer() {
    return {
      [BlockType.PARAGRAPH]: this.paragraph,
      [BlockType.HEADER]: this.header,
      [BlockType.LIST]: this.list,
      [BlockType.IMAGE]: this.image,
      [BlockType.RAW]: this.raw,
      [BlockType.CODE]: this.code,
      [BlockType.DELIMETER]: this.delimeter,
      [BlockType.ATTACHES]: this.attaches,
      [BlockType.TABLE]: this.table,
      [BlockType.EMBED]: this.embed,
      [BlockType.NAME_SCRIPT]: this.nameScriptInstructionBlock,
    };
  }

  protected get(type: BlockType): (data: BlockData) => string {
    return this.blockTypeContainer[type].bind(this);
  }

  protected generator(block: Block) {
    switch (block.type) {
      case BlockType.PARAGRAPH:
      case BlockType.HEADER:
      case BlockType.LIST:
      case BlockType.IMAGE:
      case BlockType.RAW:
      case BlockType.CODE:
      case BlockType.DELIMETER:
      case BlockType.ATTACHES:
      case BlockType.TABLE:
      case BlockType.EMBED:
      case BlockType.NAME_SCRIPT:
        this.html += this.get(block.type)(block.data);
        break;

      default:
        break;
    }
  }

  protected block(children: string) {
    return `<div class="${this.classes.container}"><div class="${this.classes.content}">${children}</div></div>`;
  }

  protected paragraph(data: BlockData) {
    const html = `<div class="${this.classes.paragraph}">${data.text}</div>`;
    if (this.params) {
      return this.block(InstructionGenerator.generate(html, this.params));
    }
    return this.block(html);
  }

  protected header(data: BlockData) {
    const html = `<h${data.level} class="${this.classes.header}">${data.text}</h${data.level}>`;
    return this.block(html);
  }

  protected list(data: BlockData) {
    const list = data.items?.map(
      (item) => `<li class="${this.classes.listItem}">${item}</li>`
    ) || [''];
    const html = data.style === 'unordered'
      ? `<ul class="${this.classes.unorderedList}">${list.join('')}</ul>`
      : `<ol class="${this.classes.orderedList}">${list.join('')}</ol>`;
    return this.block(html);
  }

  protected image(data: BlockData) {
    const html = `<img src="${data.url}" alt="${data.caption}"/><div class="text-center"><i>${data.caption}</i></div>`;
    return this.block(html);
  }

  protected raw(data: BlockData) {
    const html = `<div class="${this.classes.raw}">${data.html}</div>`;
    return this.block(html);
  }

  protected code(data: BlockData) {
    const html = `<div class="${this.classes.code}">${data.code}</div>`;
    return this.block(html);
  }

  // eslint-disable-next-line
  protected delimeter(data: BlockData) {
    const html = `<div class="${this.classes.delimeter}"></div>`;
    return this.block(html);
  }

  protected attaches(data: BlockData) {
    const html = ` <a href="${data.file.url}">${data.file.name}</a>`;
    return this.block(html);
  }

  protected table(data: BlockData) {
    let rows = '';
    data.content?.forEach((row: string[]) => {
      let cells = '';
      row.forEach((cell: string) => {
        cells += `<td class="${this.classes.cell}"><div class="${this.classes.cellContent}">${cell}</div></td>`;
      });
      rows += `<tr>${cells}</tr>`;
    });

    // eslint-disable-next-line
    const html = `<div class="${this.classes.tableWrap}"><table class="${this.classes.table}"><tbody>${rows}</tbody></table></div>`;

    return this.block(html);
  }

  protected embed(data: BlockData) {
    // eslint-disable-next-line
    const html = `<iframe src="${data.source}" width="${data.width}" height="${data.height}" frameborder="0" allow="accelerometer" allowfullscreen/><strong>${data.caption}</strong>`;

    return this.block(html);
  }

  protected nameScriptInstructionBlock(data: BlockData, isText = false) {
    const currentNameScripts = NameScript.filter((nameObj) => nameObj.title === data.text);
    const nameScriptId = currentNameScripts[0]?.value > -1 ? currentNameScripts[0].value : -1;

    let text = '';
    if (!Number.isNaN(nameScriptId) && this.params) {
      const { project } = this.params;
      if (project) {
        switch (Number(nameScriptId)) {
          default:
            break;
          case 0:
            text = project.ref.user_owner.first_name;
            break;
          case 1:
            text = project.ref.user_owner.full_name_first_last;
            break;
          case 2:
            text = project.ref.organization.common_name;
            break;
        }
      }
    }
    if (isText) {
      return text;
    }
    const html = `<div class="${this.classes.paragraph}">${text}</div>`;
    return this.block(html);
  }

  stripTags(text: string) {
    return text.replace(/(<([^>]+)>)/gi, '');
  }
}

export default EditorJsParser;
